import type { SelectType } from "../modals/custom-event";

export const months: string[] = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

export const weekDays: DayType[] = [
  "DOM",
  "LUN",
  "MAR",
  "MIE",
  "JUE",
  "VIE",
  "SAB",
];

export const weekDaysLabels: string[] = [
  "Lunes",
  "Martes",
  "Miércoles",
  "Jueves",
  "Viernes",
  "Sábado",
  "Domingo",
];

const dayMap: Record<string, number> = {
  LUN: 1,
  MAR: 2,
  MIE: 3,
  JUE: 4,
  VIE: 5,
  SAB: 6,
  DOM: 7,
};

// Crear el mapa inverso automáticamente
const reverseDayMap: Record<number, string> = Object.fromEntries(
  Object.entries(dayMap).map(([key, value]) => [value, key]),
);

export const mapDaysToNumbers = (days: string[]): number[] => {
  return days.map((d) => dayMap[d.toUpperCase()] ?? null);
};

export const mapNumbersToDays = (numbers: number[]): DayType[] => {
  return numbers.map((n) => reverseDayMap[n] ?? null) as DayType[];
};

export type DayType = "DOM" | "LUN" | "MAR" | "MIE" | "JUE" | "VIE" | "SAB";

export const getAvailableDay = (date: Date) => {
  const day = date.getDay();
  return weekDays[day];
};

type EventDayType = "HOLIDAY" | "SPECIAL_EVENT" | "NON_WORKING_DAY" | "WEEKEND";

export const SelectTypeEnum: Record<SelectType, SelectType> = {
  DAILY_INTERVAL: "DAILY_INTERVAL",
  WEEKLY_INTERVAL: "WEEKLY_INTERVAL",
  MONTHLY_INTERVAL: "MONTHLY_INTERVAL",
  YEARLY_INTERVAL: "YEARLY_INTERVAL",
  MONTHLY_BY_BUSINESS_DAY: "MONTHLY_BY_BUSINESS_DAY",
};

export type EventType = {
  id: string;
  name: string;
  description: string;
  start_date: string;
  end_date?: string;
  recurrence_type: string | null;
  type: EventDayType;
  active: boolean;
  config_type?: string | null;
  createdBy: {
    id: string;
    name: string;
    email: string;
    last_name: string;
  };
  recurrence_meta?: {
    month?: number | null;
    week?: number | null;
    weekdays?: number[] | null;
    interval?: number;
    days_of_month?: number[];
    rule?: {
      month?: number;
      week: number;
      weekday: number;
      business?: boolean;
      business_day_ordinal?: number;
      weekend_ordinal?: number;
    };
  } | null;
};

export const getDaysInMonth = (date: Date): Date[] => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const firstDay = new Date(year, month, 1);
  const startDate = new Date(firstDay);
  startDate.setDate(startDate.getDate() - firstDay.getDay());

  const days = [];
  const current = new Date(startDate);

  for (let i = 0; i < 35; i++) {
    days.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }

  return days;
};

export type EventForDay = EventType & {
  dayIndex: number;
  totalDays: number;
  dayLabel: string;
};

const MS_PER_DAY = 1000 * 60 * 60 * 24;

const toDayNumber = (date: Date): number => {
  return Math.floor(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()) /
      MS_PER_DAY,
  );
};

export const startOfDay = (date: Date): Date => {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

export const getEventsForDate = (
  events: EventType[],
  targetDate: Date,
): EventForDay[] => {
  const targetDay = toDayNumber(targetDate);

  const eventsForDay: EventForDay[] = [];

  for (const event of events) {
    if (!event.active) continue;

    const startDate = new Date(event.start_date);
    const endDate = event.end_date ? new Date(event.end_date) : startDate;

    const startDay = toDayNumber(startDate);
    const endDay = toDayNumber(endDate);

    if (targetDay < startDay || targetDay > endDay) continue;

    const totalDays = endDay - startDay + 1;
    const dayIndex = targetDay - startDay + 1;
    const dayLabel = `${dayIndex}/${totalDays}`;

    eventsForDay.push({
      ...event,
      dayIndex,
      totalDays,
      dayLabel,
    });
  }

  eventsForDay.sort((a, b) => {
    const aTime = new Date(a.start_date).getTime();
    const bTime = new Date(b.start_date).getTime();
    return aTime - bTime;
  });

  return eventsForDay.filter((event) => event.type !== "WEEKEND");
};

export const formatDate = (date: string): string => {
  const [d, m, y] = date.split("/");
  return `${y}-${m}-${d}`;
};

export const deFormatDate = (date: string): string => {
  const [y, m, d] = date.split("-");
  return `${d.padStart(2, "0")}/${m.padStart(2, "0")}/${y}`;
};

export const formatLongDate = (date: Date): string => {
  const day = date.getDate();
  const year = date.getFullYear();

  const month = months[date.getMonth()].toLowerCase();

  return `${day} de ${month}, ${year}`;
};

export const formatFullDate = (date: Date): string => {
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = String(date.getFullYear());
  return `${dd}/${mm}/${yyyy}`;
};

export const getDaysRangeFromDates = (
  startDate: Date,
  endDate: Date,
): number[] => {
  const result: number[] = [];

  const normalizeDay = (date: Date): number => {
    return date.getDay() + 1;
  };

  const current = new Date(startDate);
  current.setHours(0, 0, 0, 0);

  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);

  while (current <= end) {
    result.push(normalizeDay(current));
    current.setDate(current.getDate() + 1);
  }

  return result;
};
